import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Chip,
    CircularProgress,
    darken,
    styled,
    Typography,
} from '@mui/material';
import pluralize from 'pluralize';
import { useId, useState } from 'react';
import AppTable from './AppTable';
import type { AppTableProps } from './AppTable.types';

export interface CollapsibleAppTableProps<TRow extends object> extends AppTableProps<TRow> {
    title: string;
    pluralizeTitle?: boolean;
    pluralizeEntity?: boolean;
    loading: boolean;
    entityName?: string;
    startCollapsed?: boolean;
    disabledTableText?: string;
}

const StyledHeader = styled(AccordionSummary)(({ theme }) => ({
    background: theme.palette.primary.main,
    color: theme.palette.common.white,
    '& .MuiAccordionSummary-content': {
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    '&.Mui-expanded': {
        minHeight: 48,
    },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
    backgroundColor: darken(theme.palette.secondary.main, 0.2),
    color: theme.palette.common.white,
    marginRight: theme.spacing(1),
}));

export function CollapsibleAppTable<TRow extends object>({
    title,
    pluralizeTitle,
    pluralizeEntity,
    loading,
    entityName,
    startCollapsed,
    disabledTableText,
    scrollable = false,
    rows,
    ...tableProps
}: CollapsibleAppTableProps<TRow>) {
    const [expanded, setExpanded] = useState(startCollapsed !== true);
    const ids = useId();
    const disabled = !loading && rows.length === 0;
    const displayTitle = (pluralizeTitle ?? true) ? pluralize.plural(title) : title;
    const entityTitle = entityName ?? title;
    const summaryId = `app-table-${ids}-header`;
    const contentId = `app-table-${ids}-content`;

    return (
        <Accordion
            disabled={disabled}
            expanded={expanded && !disabled && !loading}
            onChange={(_event, nextExpanded) => setExpanded(nextExpanded)}
            disableGutters
        >
            <StyledHeader
                expandIcon={
                    loading ? (
                        <CircularProgress color="inherit" size={16} aria-label="Loading rows" />
                    ) : disabled ? (
                        false
                    ) : (
                        <span aria-hidden="true">⌄</span>
                    )
                }
                aria-controls={contentId}
                id={summaryId}
            >
                <Typography component="span">{displayTitle}</Typography>
                {disabled && (
                    <Typography component="span">
                        {disabledTableText ?? `There are no ${displayTitle.toLowerCase()}`}
                    </Typography>
                )}
                {!loading && !disabled && (
                    <StyledChip
                        label={
                            pluralizeEntity === false
                                ? `${rows.length} ${entityTitle}`
                                : pluralize(entityTitle, rows.length, true)
                        }
                        size="small"
                    />
                )}
            </StyledHeader>
            <AccordionDetails
                id={contentId}
                sx={{
                    p: 0,
                }}
            >
                <AppTable {...tableProps} rows={rows} loading={loading} scrollable={scrollable} />
            </AccordionDetails>
        </Accordion>
    );
}

export default CollapsibleAppTable;
